/**
 * Google reCAPTCHA Enterprise Utility
 *
 * Handles loading and executing reCAPTCHA for login protection.
 */

import { RECAPTCHA_CONFIG } from '@/config/app.config';

// Type declaration for the global grecaptcha object
declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

/**
 * Load the reCAPTCHA script if not already loaded
 */
async function loadRecaptchaScript(): Promise<void> {
  const siteKey = RECAPTCHA_CONFIG.siteKey;
  const scriptSrc = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;

  // Check if script is already loaded
  const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);

  if (!existingScript) {
    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    document.body.appendChild(script);

    // Wait for the script to load
    await new Promise<void>((resolve, reject) => {
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
    });
  }
}

/**
 * Execute reCAPTCHA and get a token
 *
 * @param action - The action name for reCAPTCHA (default: 'login')
 * @returns Promise<string> - The reCAPTCHA token
 *
 * @example
 * try {
 *   const token = await executeRecaptcha('login');
 *   // Use token in API request
 * } catch (error) {
 *   console.error('reCAPTCHA failed:', error);
 * }
 */
export async function executeRecaptcha(action: string = RECAPTCHA_CONFIG.action): Promise<string> {
  // Ensure script is loaded
  await loadRecaptchaScript();

  // Check if grecaptcha is available
  if (!window.grecaptcha) {
    throw new Error('reCAPTCHA is not loaded');
  }

  // Execute reCAPTCHA
  return new Promise((resolve, reject) => {
    window.grecaptcha.enterprise.ready(() => {
      window.grecaptcha.enterprise
        .execute(RECAPTCHA_CONFIG.siteKey, { action })
        .then(resolve)
        .catch(reject);
    });
  });
}

/**
 * Check if reCAPTCHA is available
 */
export function isRecaptchaAvailable(): boolean {
  return typeof window !== 'undefined' && !!window.grecaptcha;
}
